import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, Outlet, useParams } from 'react-router'

import { baseApi } from '~/shell/api/rtk/baseApi'
import { customerHolder } from '~/shell/api/rtk/customer'
import { AppHeader } from '~/shell/app/header/AppHeader'
import { forbiddenCleared } from '~/shell/redux/forbiddenSlice'
import { type RootState } from '~/shell/redux/store'
import { setLastCustomer } from '~/shell/storage/lastCustomer'

/**
 * Everything scoped to one customer, mounted fresh for each.
 *
 * Its only job is the teardown: when this instance is replaced by the next customer's,
 * the cleanup discards the cached API responses and any recorded 403, so neither
 * crosses a customer boundary. Doing it in cleanup rather than in the parent's effect
 * is what makes the ordering work — react runs an outgoing subtree's cleanups before
 * the incoming subtree's effects, and it is those effects that subscribe the queries.
 * Resetting afterwards would wipe the new customer's in-flight requests instead of the
 * old customer's stale ones.
 *
 * Nothing happens on the first mount: there is no previous customer, so there is
 * nothing stale to clear.
 */
const CustomerScope = () => {
  const dispatch = useDispatch()

  useEffect(
    () => () => {
      dispatch(baseApi.util.resetApiState())
      // A new customer selection starts authorized; a 403 recorded against the
      // previous one says nothing about this one.
      dispatch(forbiddenCleared())
    },
    [dispatch],
  )

  return (
    <>
      <AppHeader />
      <Outlet />
    </>
  )
}

/**
 * Layout for every customer-scoped Scout route (`/c/:customer/`): the app header above
 * the routed page, plus the customer plumbing the queries beneath it depend on.
 *
 * Responsibilities:
 *   - Publish the route's customer to the holder bridge, so the out-of-React RTK Query
 *     base query can scope requests it is not passed a customer for (SHELL-14). Done
 *     during render rather than in an effect because the queries beneath fire from their
 *     own effects, which run first.
 *   - Remount the whole subtree on a customer switch, by keying it on the customer, so
 *     nothing belonging to the previous one survives (SHELL-16): the queries resubscribe
 *     and refetch, and per-customer component state — scroll position, filters, an open
 *     flyout — is discarded rather than reinterpreted against different data. Clearing
 *     the cache alone would not do this; `resetApiState` empties the store but leaves
 *     already-mounted hooks subscribed, so the previous customer's data would stay on
 *     screen until something else forced a refetch.
 *
 *   - Persist the active customer as the last-viewed default, so even a direct URL visit
 *     (no picker) seeds the next session's bootstrap.
 *   - Redirect to the unauthorized page when a customer-scoped request has been rejected
 *     with a server-enforced 403. The `:customer` segment is only a selector; outpatient's
 *     `authorize_customer_access` is the real boundary, so a customer this session may not
 *     reach is caught here from the 403 rather than blocked client-side. This is what reads
 *     the flag `forbiddenMiddleware` sets — without it a 403 left the user on the header
 *     over a broken page with nothing explaining why.
 *
 * Mirrors arc's `CustomerLayout`.
 *
 * The header's own nav resolves the active app itself (`ActiveAppNav`), so nothing about it is threaded
 * through here; this layout is what makes it possible by remounting on a customer switch, which is the
 * event that invalidates one customer's nav items as much as its pages.
 */
export const CareGapsLayout = () => {
  const { customer } = useParams<{ customer: string }>()
  const isForbidden = useSelector(
    (state: RootState) => state.forbidden.isForbidden,
  )

  if (customer !== undefined) {
    customerHolder.set(customer)
  }

  useEffect(() => {
    if (customer !== undefined) {
      setLastCustomer(customer)
    }
  }, [customer])

  if (isForbidden) {
    return <Navigate replace to="/unauthorized" />
  }

  return <CustomerScope key={customer} />
}
